/*
  Product comparison feature (v1: Alesis electronic drum kits, scoped by whichever
  products have a populated `custom.spec_sheet` metafield -- see snippets/product-item.liquid
  and snippets/product-info.liquid for where the toggle button is only rendered when that
  metafield is present).

  Storage follows the same convention as the existing "recently viewed" feature
  (theme:recently-viewed-products in assets/theme.js / snippets/js-variables.liquid):
  a capped, deduped JSON array in localStorage, wrapped in try/catch for Safari
  private-mode/storage-disabled environments.
*/
(function () {
  const STORAGE_KEY = 'theme:compare-products';
  const MAX_ITEMS = 4;

  function readList() {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch (e) {
      return [];
    }
  }

  function writeList(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      // Safari private mode / storage disabled -- fail silently, feature just won't persist.
    }
    window.dispatchEvent(new CustomEvent('compare:change', { detail: { list } }));
  }

  function isInCompare(id) {
    return readList().some((item) => item.id === id);
  }

  function addToCompare(id, handle) {
    const list = readList();
    if (list.some((item) => item.id === id)) return list;
    if (list.length >= MAX_ITEMS) return list;
    list.push({ id, handle });
    writeList(list);
    return list;
  }

  function removeFromCompare(id) {
    const list = readList().filter((item) => item.id !== id);
    writeList(list);
    return list;
  }

  window.cyanCompare = { readList, writeList, isInCompare, addToCompare, removeFromCompare, MAX_ITEMS };

  class CyanCompareToggle extends HTMLElement {
    connectedCallback() {
      this.productId = this.getAttribute('data-product-id');
      this.productHandle = this.getAttribute('data-product-handle');
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.updateState();
      this.onClick = this.onClick.bind(this);
      this.button.addEventListener('click', this.onClick);
      this.onExternalChange = () => this.updateState();
      window.addEventListener('compare:change', this.onExternalChange);
    }

    disconnectedCallback() {
      window.removeEventListener('compare:change', this.onExternalChange);
    }

    onClick(event) {
      event.preventDefault();
      if (isInCompare(this.productId)) {
        removeFromCompare(this.productId);
      } else {
        const list = readList();
        if (list.length >= MAX_ITEMS) {
          this.button.classList.add('is-at-limit');
          window.setTimeout(() => this.button.classList.remove('is-at-limit'), 1200);
          return;
        }
        addToCompare(this.productId, this.productHandle);
      }
      this.updateState();
    }

    updateState() {
      const active = isInCompare(this.productId);
      this.button.classList.toggle('is-active', active);
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = this.button.querySelector('.cyan-compare-toggle__label');
      if (label) {
        label.textContent = active ? label.dataset.added : label.dataset.default;
      }
    }
  }

  // Links to /pages/alesis rather than the older, narrower /pages/compare
  // (still used elsewhere and scoped to a generic "electronic-drums"
  // collection): every product that can currently be added to compare is
  // Alesis-only, and /pages/alesis's own comparison section already covers
  // that full, categorized catalog. Sending shoppers to the narrower page
  // instead showed a visibly reduced product grid (fewer category tabs) the
  // moment they removed items back down to the browse view, which read as a
  // broken page rather than a different, older one.
  class CyanCompareBar extends HTMLElement {
    connectedCallback() {
      this.render();
      this.onChange = () => this.render();
      window.addEventListener('compare:change', this.onChange);
    }

    disconnectedCallback() {
      window.removeEventListener('compare:change', this.onChange);
    }

    render() {
      const list = readList();
      if (!list.length) {
        this.hidden = true;
        this.innerHTML = '';
        return;
      }
      this.hidden = false;
      this.innerHTML =
        '<a class="cyan-compare-bar__link" href="/pages/alesis">' +
          '<span class="cyan-compare-bar__count">' + list.length + '</span>' +
          '<span class="cyan-compare-bar__text">Compare</span>' +
          '<svg class="cyan-compare-bar__arrow" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4 2l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</a>';
    }
  }

  /*
    The comparison page itself. Cross-references the server-rendered catalog
    (every comparable product's data + spec_sheet, embedded by
    sections/cyan-product-comparison.liquid) against the shopper's localStorage
    selection, and builds the table client-side -- avoids needing metafields
    over a storefront AJAX call, since Liquid has no generic way to read
    arbitrary URL query params for this to be rendered fully server-side instead.
  */
  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  // Two vocabularies of custom.spec_sheet labels exist across the catalog:
  // older kits use granular boolean/numeric fields (Mesh, USB/MIDI, Bluetooth,
  // Factory kits, User kits, Toms, Crash cymbals, Ride zones, Kick tower),
  // newer ones use free-text summary fields (Pads, Cymbals, Module, Sounds,
  // Connectivity, Power) that this table's row order (section setting
  // "label_order") is built around. Looking a row label up by exact match
  // alone left every older kit showing "-" for Pads/Cymbals/Sounds/Connectivity
  // even though the same facts exist under the older field names -- this
  // recombines those granular fields into the equivalent summary line instead
  // of requiring every product's data to be rewritten into one vocabulary.
  // Module and Power have no older-vocabulary equivalent to derive from, so
  // those two legitimately fall back to "-" for kits that never recorded them.
  function findSpec(specSheet, label) {
    const entry = (specSheet || []).find((s) => s.label === label);
    return entry ? entry.value : null;
  }

  function resolveSpecValue(product, label) {
    const specSheet = product.specSheet || [];
    const direct = findSpec(specSheet, label);
    if (direct) return direct;

    switch (label) {
      case 'Pads': {
        const parts = [];
        if (findSpec(specSheet, 'Mesh') === 'Yes') parts.push('Mesh heads');
        const toms = findSpec(specSheet, 'Toms');
        if (toms) parts.push(toms + (toms === '1' ? ' tom' : ' toms'));
        if (findSpec(specSheet, 'Kick tower') === 'Yes') parts.push('Mesh kick tower');
        return parts.join('; ') || null;
      }
      case 'Cymbals': {
        const parts = [];
        const crash = findSpec(specSheet, 'Crash cymbals');
        if (crash) parts.push(crash + (crash === '1' ? ' crash cymbal' : ' crash cymbals'));
        const ride = findSpec(specSheet, 'Ride zones');
        if (ride) parts.push(ride + '-zone ride');
        return parts.join('; ') || null;
      }
      case 'Sounds': {
        const parts = [];
        const factory = findSpec(specSheet, 'Factory kits');
        if (factory) parts.push(factory + ' factory kits');
        const user = findSpec(specSheet, 'User kits');
        if (user) parts.push((user.toLowerCase() === 'unlimited' ? 'unlimited' : user) + ' user kits');
        return parts.join(' + ') || null;
      }
      case 'Connectivity': {
        const parts = [];
        if (findSpec(specSheet, 'USB/MIDI') === 'Yes') parts.push('USB/MIDI');
        if (findSpec(specSheet, 'Bluetooth') === 'Yes') parts.push('Bluetooth');
        return parts.join('; ') || null;
      }
      default:
        return null;
    }
  }

  // Feature chips: prefer the product's own short keyFeatures (from
  // custom.product_details) since those are written per-product and read
  // naturally as chips; fall back to a generic Mesh/USB-MIDI/Bluetooth scan of
  // the spec_sheet text for older products that don't have keyFeatures yet.
  function deriveChips(product) {
    const keyFeatures = (product.details && product.details.keyFeatures) || [];
    if (keyFeatures.length) {
      return keyFeatures.slice(0, 3).map((f) => (f.length > 24 ? f.slice(0, 22) + '…' : f));
    }
    const haystack = (product.specSheet || []).map((s) => s.value).join(' ').toLowerCase();
    const chips = [];
    if (haystack.includes('mesh')) chips.push('Mesh');
    if (haystack.includes('usb') || haystack.includes('midi')) chips.push('USB/MIDI');
    if (haystack.includes('bluetooth')) chips.push('Bluetooth');
    return chips;
  }

  // Pills: New / Discontinued, sourced from custom.alesis_badges.
  // Falls back to the older custom.legacy_status metafield for Discontinued
  // so products only tagged the old way still show correctly.
  function derivePills(product) {
    const badges = product.badges || {};
    const pills = [];
    if (badges.isNew) pills.push({ text: badges.isNewLabel || 'New', variant: 'new' });
    if (badges.discontinued || product.legacy === 'discontinued') {
      pills.push({ text: badges.discontinuedLabel || 'Discontinued', variant: 'discontinued' });
    }
    return pills;
  }

  class CyanComparePage extends HTMLElement {
    connectedCallback() {
      const catalogEl = document.getElementById('cyan-compare-catalog');
      const labelsEl = document.getElementById('cyan-compare-labels');
      try {
        this.catalog = catalogEl ? JSON.parse(catalogEl.textContent) : [];
        this.labelOrder = labelsEl ? JSON.parse(labelsEl.textContent).map((l) => l.trim()).filter(Boolean) : [];
      } catch (e) {
        this.catalog = [];
        this.labelOrder = [];
      }

      this.emptyEl = this.querySelector('[data-compare-empty]');
      this.tableWrapEl = this.querySelector('[data-compare-table-wrap]');
      this.tableEl = this.querySelector('[data-compare-table]');
      this.gridEl = this.querySelector('[data-compare-grid]');
      this.tabsEl = this.querySelector('[data-compare-tabs]');
      this.activeTab = 'all';

      const wrap = this.closest('.cyan-compare-page-wrap') || document;
      this.modalEl = wrap.querySelector('[data-compare-modal]');
      this.modalPanelEl = wrap.querySelector('[data-compare-modal-panel]');
      const modalCloseEl = wrap.querySelector('[data-compare-modal-close]');
      if (modalCloseEl) {
        modalCloseEl.addEventListener('click', () => this.closeModal());
      }
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeModal();
      });

      this.renderTabs();
      this.renderGrid();
      this.render();
      this.onChange = () => this.render();
      window.addEventListener('compare:change', this.onChange);
    }

    disconnectedCallback() {
      window.removeEventListener('compare:change', this.onChange);
    }

    // Tabs are built from whatever categories + New / Discontinued flags
    // actually appear in the catalog, so the tab list stays accurate as
    // products are added without needing a hardcoded list anywhere.
    renderTabs() {
      if (!this.tabsEl) return;
      const categories = Array.from(new Set(
        this.catalog.map((p) => (p.badges && p.badges.category) || null).filter(Boolean)
      ));
      const hasNew = this.catalog.some((p) => p.badges && p.badges.isNew);
      const hasDiscontinued = this.catalog.some((p) => (p.badges && p.badges.discontinued) || p.legacy === 'discontinued');

      const tabs = [{ key: 'all', label: 'All' }]
        .concat(categories.map((c) => ({ key: 'category:' + c, label: c })))
        .concat(hasNew ? [{ key: 'new', label: "What's New" }] : [])
        .concat(hasDiscontinued ? [{ key: 'discontinued', label: 'Discontinued' }] : []);

      if (tabs.length <= 1) {
        this.tabsEl.hidden = true;
        return;
      }

      this.tabsEl.innerHTML = tabs.map((t) =>
        '<button type="button" class="cyan-compare-tab' + (t.key === this.activeTab ? ' is-active' : '') + '" data-tab-key="' + t.key + '">' + escapeHtml(t.label) + '</button>'
      ).join('');

      this.tabsEl.querySelectorAll('[data-tab-key]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.activeTab = btn.getAttribute('data-tab-key');
          this.tabsEl.querySelectorAll('[data-tab-key]').forEach((b) => b.classList.toggle('is-active', b === btn));
          this.filterGrid();
        });
      });
    }

    filterGrid() {
      if (!this.gridEl) return;
      this.gridEl.querySelectorAll('[data-card-id]').forEach((card) => {
        const id = card.getAttribute('data-card-id');
        const product = this.catalog.find((p) => String(p.id) === String(id));
        card.hidden = !this.cardMatchesTab(product);
      });
    }

    cardMatchesTab(product) {
      if (this.activeTab === 'all') return true;
      const badges = (product && product.badges) || {};
      if (this.activeTab === 'new') return !!badges.isNew;
      if (this.activeTab === 'discontinued') return !!badges.discontinued || (product && product.legacy === 'discontinued');
      if (this.activeTab.startsWith('category:')) return badges.category === this.activeTab.slice(9);
      return true;
    }

    renderGrid() {
      if (!this.gridEl) return;
      if (!this.catalog.length) {
        this.emptyEl.hidden = false;
        return;
      }
      this.emptyEl.hidden = true;

      const cardsHtml = this.catalog.map((p) => {
        const chips = deriveChips(p).map((c) => '<span class="cyan-compare-card__chip">' + escapeHtml(c) + '</span>').join('');
        const pills = derivePills(p).map((pill) =>
          '<span class="cyan-compare-card__badge cyan-compare-card__badge--' + pill.variant + '">' + escapeHtml(pill.text) + '</span>'
        ).join('');
        return (
          '<div class="cyan-compare-card" data-card-id="' + p.id + '">' +
            (pills ? '<div class="cyan-compare-card__badges">' + pills + '</div>' : '') +
            '<button type="button" class="cyan-compare-card__image" data-open-detail="' + p.id + '" aria-label="View details for ' + escapeHtml(p.title) + '">' +
              (p.image ? '<img src="' + p.image + '" alt="" loading="lazy">' : '') +
            '</button>' +
            '<div class="cyan-compare-card__body">' +
              '<div class="cyan-compare-card__heading">' +
                '<button type="button" class="cyan-compare-card__title" data-open-detail="' + p.id + '">' + escapeHtml(p.title) + '</button>' +
                '<span class="cyan-compare-card__price"><span class="cyan-aed-icon" aria-hidden="true"></span>' + escapeHtml(p.price.replace(/[^0-9.,]/g, '')) + '</span>' +
              '</div>' +
              (p.barcode ? '<span class="cyan-compare-card__upc">UPC: ' + escapeHtml(p.barcode) + '</span>' : '') +
              (chips ? '<div class="cyan-compare-card__chips">' + chips + '</div>' : '') +
              '<label class="cyan-compare-card__toggle">' +
                '<input type="checkbox" data-compare-checkbox="' + p.id + '" data-handle="' + escapeHtml(p.handle) + '">' +
                '<span>Add to compare</span>' +
              '</label>' +
            '</div>' +
          '</div>'
        );
      }).join('');

      this.gridEl.insertAdjacentHTML('beforeend', cardsHtml);

      this.gridEl.querySelectorAll('[data-open-detail]').forEach((el) => {
        el.addEventListener('click', () => this.openModal(el.getAttribute('data-open-detail')));
      });

      this.gridEl.querySelectorAll('[data-compare-checkbox]').forEach((checkbox) => {
        const id = checkbox.getAttribute('data-compare-checkbox');
        checkbox.checked = isInCompare(id);
        checkbox.addEventListener('click', (event) => event.stopPropagation());
        checkbox.addEventListener('change', () => {
          if (checkbox.checked) {
            const list = readList();
            if (list.length >= window.cyanCompare.MAX_ITEMS) {
              checkbox.checked = false;
              return;
            }
            addToCompare(id, checkbox.getAttribute('data-handle'));
          } else {
            removeFromCompare(id);
          }
        });
      });

      window.addEventListener('compare:change', () => {
        this.gridEl.querySelectorAll('[data-compare-checkbox]').forEach((checkbox) => {
          checkbox.checked = isInCompare(checkbox.getAttribute('data-compare-checkbox'));
        });
      });
    }

    openModal(id) {
      const product = this.catalog.find((p) => String(p.id) === String(id));
      if (!product || !this.modalEl || !this.modalPanelEl) return;

      const details = product.details || {};
      const description = details.description || '';
      const keyFeatures = details.keyFeatures || [];
      const included = details.included || [];
      const notIncluded = details.notIncluded || [];
      const quickSpecs = (product.specSheet || []).filter((s) => s.label !== 'Included Accessories');

      const section = (title, bodyHtml) => bodyHtml ? (
        '<div class="cyan-compare-modal__section">' +
          '<h4>' + title + '</h4>' +
          bodyHtml +
        '</div>'
      ) : '';

      const list = (items) => '<ul>' + items.map((i) => '<li>' + escapeHtml(i) + '</li>').join('') + '</ul>';

      const specsTable = quickSpecs.length
        ? '<table class="cyan-compare-modal__specs">' +
            quickSpecs.map((s) => '<tr><th>' + escapeHtml(s.label) + '</th><td>' + escapeHtml(s.value) + '</td></tr>').join('') +
          '</table>'
        : '';

      this.modalPanelEl.innerHTML =
        '<button type="button" class="cyan-compare-modal__close" data-compare-modal-close aria-label="Close">Close <span aria-hidden="true">&times;</span></button>' +
        '<div class="cyan-compare-modal__image">' + (product.largeImage ? '<img src="' + product.largeImage + '" alt="">' : '') + '</div>' +
        '<div class="cyan-compare-modal__content">' +
          '<div class="cyan-compare-modal__meta">' +
            '<span class="cyan-compare-modal__price"><span class="cyan-aed-icon" aria-hidden="true"></span>' + escapeHtml(product.price.replace(/[^0-9.,]/g, '')) + '</span>' +
            (product.barcode ? '<span class="cyan-compare-modal__upc">UPC: ' + escapeHtml(product.barcode) + '</span>' : '') +
          '</div>' +
          '<h3 class="cyan-compare-modal__title">' + escapeHtml(product.title) + '</h3>' +
          section('Description', description ? '<p>' + escapeHtml(description) + '</p>' : '') +
          section('Key Features', keyFeatures.length ? list(keyFeatures) : '') +
          section('What’s Included', included.length ? list(included) : '') +
          section('What’s NOT Included', notIncluded.length ? list(notIncluded) : '') +
          section('Quick Specs', specsTable) +
          '<a class="button button--primary cyan-compare-modal__buy" href="' + product.url + '">View product</a>' +
        '</div>';

      this.modalPanelEl.querySelector('[data-compare-modal-close]').addEventListener('click', () => this.closeModal());
      this.modalEl.hidden = false;
      document.documentElement.classList.add('cyan-compare-modal-open');
    }

    closeModal() {
      if (!this.modalEl) return;
      this.modalEl.hidden = true;
      document.documentElement.classList.remove('cyan-compare-modal-open');
    }

    render() {
      const selected = readList();
      const products = selected
        .map((item) => this.catalog.find((p) => String(p.id) === String(item.id)))
        .filter(Boolean);

      if (!products.length) {
        this.tableWrapEl.hidden = true;
        return;
      }
      this.tableWrapEl.hidden = false;

      const rows = [];

      const headerCells = products.map((p) => {
        const legacyNote = p.legacy === 'discontinued'
          ? '<span class="cyan-compare-table__legacy">Discontinued</span>'
          : '';
        return (
          '<th class="cyan-compare-table__product-cell">' +
            '<button type="button" class="cyan-compare-table__remove" data-remove-id="' + p.id + '" aria-label="Remove ' + p.title + ' from comparison">&times;</button>' +
            '<a href="' + p.url + '">' +
              (p.image ? '<img src="' + p.image + '" alt="" loading="lazy">' : '') +
              '<span class="cyan-compare-table__title">' + p.title + '</span>' +
            '</a>' +
            legacyNote +
            '<span class="cyan-compare-table__price">' + p.price + '</span>' +
          '</th>'
        );
      });
      rows.push('<tr><th></th>' + headerCells.join('') + '</tr>');

      this.labelOrder.forEach((label) => {
        const cells = products.map((p) => {
          const value = resolveSpecValue(p, label);
          return '<td>' + (value ? escapeHtml(value) : '—') + '</td>';
        });
        rows.push('<tr><th class="cyan-compare-table__row-label">' + label + '</th>' + cells.join('') + '</tr>');
      });

      this.tableEl.innerHTML = rows.join('');
      this.tableEl.querySelectorAll('[data-remove-id]').forEach((btn) => {
        btn.addEventListener('click', () => removeFromCompare(btn.getAttribute('data-remove-id')));
      });
    }
  }

  if (!customElements.get('cyan-compare-toggle')) {
    customElements.define('cyan-compare-toggle', CyanCompareToggle);
  }
  if (!customElements.get('cyan-compare-bar')) {
    customElements.define('cyan-compare-bar', CyanCompareBar);
  }
  if (!customElements.get('cyan-compare-page')) {
    customElements.define('cyan-compare-page', CyanComparePage);
  }
})();
